import { useCallback, useMemo, useSyncExternalStore } from "react";
import type { ResourceClient } from "./resource-client.js";
import type { AgentStore } from "./store.js";
import type { AgentStoreSnapshot, ConnectionStatus, ResourceClientSnapshot } from "./types.js";

/**
 * Subscribe to an {@link AgentStore} and re-render whenever state changes.
 *
 * Uses `useSyncExternalStore` internally for concurrent-mode safety. The
 * returned snapshot is the same object reference until the next state change,
 * so destructuring is stable across renders.
 *
 * @param store - The store to subscribe to.
 * @returns Current immutable state snapshot.
 *
 * @example
 * ```tsx
 * function ChatPanel({ store }: { store: AgentStore }) {
 *   const { messages, streamingText, phase } = useAgentStore(store)
 *   // ...
 * }
 * ```
 * @docLink packages/store/concepts#react-hooks
 */
export function useAgentStore(store: AgentStore): AgentStoreSnapshot {
  const subscribe = useCallback(
    (onStoreChange: () => void) => store.subscribe(onStoreChange),
    [store],
  );
  const getSnapshot = useCallback(() => store.getSnapshot(), [store]);
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}

/**
 * Return stable action dispatchers bound to an {@link AgentStore}.
 *
 * The returned object is referentially stable across renders (memoised on
 * `store` identity), so it is safe to pass as a dependency to `useCallback`
 * or `useMemo` without causing unnecessary re-computations.
 *
 * @param store - The store to bind actions to.
 * @returns Object with `prompt`, `reply`, `cancel`, `loadPreviousMessages`,
 *   and `setStatus` action functions.
 *
 * @example
 * ```tsx
 * function ChatInput({ store }: { store: AgentStore }) {
 *   const { prompt, cancel } = useAgentStoreActions(store)
 *   return <button onClick={() => prompt('Hello')}>Send</button>
 * }
 * ```
 * @docLink packages/store/concepts#react-hooks
 */
export function useAgentStoreActions(store: AgentStore) {
  return useMemo(
    () => ({
      /** Send a user prompt to the agent. */
      prompt: (text: string) => store.prompt(text),
      /**
       * Send a reply to the agent's pending question. Pass `question` verbatim
       * from the `question` event being answered when the UI can surface
       * several at once — see {@link AgentStore.reply}.
       */
      reply: (answer: string, question?: string, requestId?: string) =>
        store.reply(answer, question, requestId),
      /** Cancel the current agent turn. */
      cancel: () => store.cancel(),
      /** Load the previous page of messages. */
      loadPreviousMessages: () => store.loadPreviousMessages(),
      /** Programmatically update the connection status. */
      setStatus: (status: ConnectionStatus) => store.setStatus(status),
    }),
    [store],
  );
}

/**
 * Subscribe to a {@link ResourceClient} and re-render when resources change.
 *
 * Uses `useSyncExternalStore` for concurrent-mode safety. Re-renders only
 * when the agent emits a `resources_available` event (typically once on connect).
 *
 * @param client - The resource client to subscribe to.
 * @returns Current immutable resource snapshot (mounts + connectors).
 *
 * @example
 * ```tsx
 * function ResourcePanel({ client }: { client: ResourceClient }) {
 *   const { mounts, connectors } = useResourceClient(client)
 *   // ...
 * }
 * ```
 * @docLink packages/store/concepts#react-hooks
 */
export function useResourceClient(client: ResourceClient): ResourceClientSnapshot {
  const subscribe = useCallback(
    (onStoreChange: () => void) => client.subscribe(onStoreChange),
    [client],
  );
  const getSnapshot = useCallback(() => client.getSnapshot(), [client]);
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}
